/**
 * /number-systems — системы счисления с решением.
 *
 * Конвертеров, которые печатают ответ, сотни. Школьнику нужна ЗАПИСЬ перевода:
 * лесенка деления с остатками, развёрнутая форма, триады и тетрады — такая,
 * какую принимает учитель информатики. Здесь четыре режима:
 *   convert   перевод всеми школьными способами + живой мультиконвертер;
 *   arith     + − × ÷ столбиком в любом основании, побитовые операции;
 *   computer  прямой/обратный/дополнительный код, IEEE 754, объём информации;
 *   exam      типовые задачи ЕГЭ/ОГЭ, тренажёр с разбором ошибки, лист заданий.
 *
 * ⚠️ Математика — в модулях без DOM (num/convert/arith/codes/ieee/exam/trainer),
 * они покрыты node-тестами с оракулами. Здесь только связывание со страницей.
 */

import * as convert from './mode-convert.js';
import * as arith from './mode-arith.js';
import * as computer from './mode-computer.js';
import * as exam from './mode-exam.js';
import { $, t, ae, stage, pdfReady, sheetText, sheetModel, saveHash, saveHashNow, readHash, seg, settings, loadSetting, saveSetting, SLUG, drawArrows, locNum, locText, DEC } from './ui.js';

const MODES = { convert, arith, computer, exam };
let mode = 'convert';
let modeSeg;

function setMode(m, opts = {}) {
    if (!MODES[m]) m = 'convert';
    const apply = () => {
        mode = m;
        modeSeg.set(m);
        document.querySelectorAll('[data-ns-pane]').forEach((p) => { p.hidden = p.getAttribute('data-ns-pane') !== m; });
        if (!opts.silent) MODES[m].render(false);
    };
    if (opts.animate && window.__viewTransition) window.__viewTransition(apply);
    else apply();
}

function persist() {
    const st = MODES[mode].state();
    saveHash(Object.assign({ m: mode }, st));
}

/* ── выдача ──────────────────────────────────────────────────────────────── */

function copySolution() {
    const text = sheetText();
    if (!text) return;
    // Запись в буфер — в том же стеке, что нажатие (Safari иначе отказывает).
    const p = window.__copyText ? window.__copyText(text) : navigator.clipboard.writeText(text).then(() => true, () => false);
    Promise.resolve(p).then((ok) => {
        if (window.__toast) window.__toast(t(ok ? 'copied' : 'copy_failed'), ok ? 'success' : 'error');
        ae(ok ? 'copy' : 'copy_failed', { mode, len: text.length });
    });
}

/** Лист PDF — клон сцены: условие, ход, ответ, проверка. Без кнопок и переключателей. */
function buildReport() {
    const root = document.createElement('div');
    root.className = 'lg-app ns ns-report';
    root.style.cssText = 'padding:0;background:#fff;color:#111;max-width:none';
    const sheet = $('nsSheet').cloneNode(true);
    sheet.removeAttribute('id');
    sheet.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    sheet.querySelectorAll('.ns-methods, .ns-notation, .tc-head, details').forEach((el) => el.remove());
    sheet.style.cssText = 'background:#fff;border:0;box-shadow:none;backdrop-filter:none;padding:0';
    root.appendChild(sheet);
    return root;
}

function pdfName() {
    const m = sheetModel();
    const base = (m && m.answer ? m.answer : 'number-systems').replace(/[^0-9A-Za-z.,=+-]+/g, '').slice(0, 40) || 'number-systems';
    try {
        if (window.__outputName) return window.__outputName.join(window.__outputName.sanitize(base), 'pdf');
    } catch (_) { /* примитив не загрузился — имя ниже */ }
    return 'number-systems.pdf';
}

function savePdf(btn) {
    if (!sheetText() || !pdfReady('sheet')) return;
    btn.disabled = true;
    window.__generatePDF({
        element: buildReport(),
        title: t('pdf_title'),
        filename: pdfName(),
        strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => {
        btn.disabled = false;
        // tool_complete уже ушёл, когда ответ показали (resultShown): PDF — выдача, а не второй результат.
        ae('pdf', { ok: !!(r && r.ok), code: r && r.code, mode });
    }, () => { btn.disabled = false; });
}

function copyLink(url) {
    const p = window.__copyText ? window.__copyText(url) : navigator.clipboard.writeText(url).then(() => true, () => false);
    Promise.resolve(p).then((ok) => {
        if (window.__toast) window.__toast(t(ok ? 'link_copied' : 'copy_failed'), ok ? 'success' : 'error');
        ae('share', { via: 'clipboard', ok: ok ? 1 : 0, mode });
    });
}

function share() {
    // Ссылка собирается СРАЗУ, без ожидания: Safari отдаёт буфер обмена и окно «Поделиться» только
    // в том же стеке, что нажатие. Прежняя пауза в 300 мс (ждали отложенную запись адреса)
    // превращала кнопку на iPhone в «Не удалось скопировать».
    const url = saveHashNow(Object.assign({ m: mode }, MODES[mode].state()));
    if (navigator.share && matchMedia('(pointer:coarse)').matches) {
        let asked;
        try { asked = navigator.share({ title: document.title, url }); } catch (_) { asked = null; }
        if (asked && asked.then) {
            asked.then(() => ae('share', { via: 'native', ok: 1, mode }), (e) => {
                if (e && e.name === 'AbortError') { ae('share', { via: 'native', ok: 0, mode }); return; }
                copyLink(url);
            });
            return;
        }
    }
    copyLink(url);
}

function printSheet() {
    ae('print', { mode });
    window.print();
}

/* ── запуск ──────────────────────────────────────────────────────────────── */

function init() {
    if (!$('nsApp')) return;
    // Примеры в разметке записаны с точкой; подпись — с разделителем локали
    // (тот же DEC, которым пишется ответ): «0,1 → 2» в ru/de/fr, «0.1 → 2» в en.
    document.querySelectorAll('[data-ns-num]').forEach((el) => { el.textContent = locNum(el.textContent); });
    // Подсказки в полях отрисованы сервером знаком перевода («p. ej., 75; 0,1»): на испанской
    // странице знак зависит от страны читателя (Мексика — 0.1), и подсказка идёт за ним.
    document.querySelectorAll('#nsApp input[placeholder]').forEach((el) => { el.setAttribute('placeholder', locText(el.getAttribute('placeholder'))); });
    // Вид записи — школьная традиция страны: выбор, сделанный на русской странице
    // (лесенка), не должен перебивать short division на английской. Ключ — по локали.
    const notationKey = 'notation_' + String(window.__LOCALE || 'en');
    settings.notation = loadSetting(notationKey, settings.notation);
    $('nsNotation').value = settings.notation;
    if (!$('nsNotation').value) { settings.notation = 'ladder'; $('nsNotation').value = 'ladder'; }
    $('nsNotation').addEventListener('change', () => {
        settings.notation = $('nsNotation').value;
        saveSetting(notationKey, settings.notation);
        MODES[mode].render(true);
        ae('notation', { n: settings.notation });
    });

    const ctx = {
        onChanged: persist,
        openArith: (params) => {
            arith.restore(new URLSearchParams(params));
            setMode('arith', { animate: true });
            ae('open_in_arith', {});
        },
        openConvert: (n, f, q) => {
            convert.openTask(n, f, q);
            setMode('convert', { animate: true });
            ae('open_in_convert', {});
        },
    };
    Object.values(MODES).forEach((m) => m.init(ctx));

    modeSeg = seg(document.querySelector('.ns-modes'), 'data-ns-mode', (m) => {
        setMode(m, { animate: true });
        persist();
        ae('mode', { m });
    });

    document.querySelectorAll('[data-ns-act]').forEach((b) => b.addEventListener('click', () => {
        const a = b.getAttribute('data-ns-act');
        if (a === 'copy') copySolution();
        else if (a === 'pdf') savePdf(b);
        else if (a === 'print') printSheet();
        else if (a === 'share') share();
    }));

    const q = readHash();
    if (q && q.get('m') && MODES[q.get('m')]) {
        const m = q.get('m');
        try { MODES[m].restore(q); } catch (e) {
            // Битая ссылка не должна ронять страницу: режим открывается с умолчаниями, причина — в телеметрию.
            try { if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'restore_failed', mode: m, why: String(e && e.message || e).slice(0, 60) }); } catch (_) { /* телеметрия не мешает работе */ }
        }
        if (m !== 'convert') convert.setDefault();
        setMode(m, { silent: true });
    } else {
        convert.setDefault();
        setMode('convert', { silent: true });
    }
    MODES[mode].render(false);
    stage('ready');

    let raf = 0;
    window.addEventListener('resize', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(drawArrows);
    });

    // Опора для сторожей: текущий режим и текст листа видны снаружи.
    window.__nsState = () => ({ mode, sheet: sheetText(), model: sheetModel(), dec: DEC });
    window.__nsTrainer = exam.trainerTask;
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

export { SLUG };
